import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { authApi } from '../api/client';
import { Image } from '../components/common/NextAdapters';
import styles from './LoginPage.module.css';
import { Toaster } from 'react-hot-toast';
import toast from 'react-hot-toast';
import { getApiErrorMessage } from '../api/errors';
import { usePageTitle } from '../hooks/usePageTitle';
import { FiEye, FiEyeOff, FiMail, FiLock, FiUser, FiAtSign, FiFilm, FiCheckCircle } from 'react-icons/fi';

// Dedicated content-creator sign-up page. Deliberately NOT linked from the main registration
// page, the nav, or anywhere else in the app — it's only reachable by whoever has this exact
// URL (e.g. shared directly by an admin with a prospective creator). Registering here always
// requests the ContentCreator role; upload access itself is still granted separately by an
// admin after review (AppUser.CanUploadContent) — see AdminContentCreatorsPage.
export default function CreatorSignupPage() {
  usePageTitle('Become a Creator · Kast');
  const [fullName, setFullName] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pendingActivationEmail, setPendingActivationEmail] = useState(null);
  const [resendLoading, setResendLoading] = useState(false);

  const handleResendActivation = async () => {
    if (!pendingActivationEmail) return;
    setResendLoading(true);
    try {
      await authApi.resendActivation(pendingActivationEmail);
      toast.success('Activation email sent — check your inbox.');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not resend the activation email.'));
    } finally {
      setResendLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) { toast.error('Please enter your email'); return; }
    if (!username.trim()) { toast.error('Please choose a username'); return; }
    if (!password) { toast.error('Please enter your password'); return; }
    if (!fullName.trim()) { toast.error('Please enter your full name'); return; }
    if (!/^[a-zA-Z0-9_]{3,20}$/.test(username.trim())) {
      toast.error('Username must be 3–20 characters: letters, numbers, underscores only');
      return;
    }

    setLoading(true);
    try {
      const response = await authApi.register(fullName.trim(), username.trim(), email, password, 'ContentCreator');
      const { data } = response;

      if (data.success) {
        setPendingActivationEmail(email);
        toast.success('Creator account created! Check your email to activate it.');
      } else {
        toast.error(data.message || 'Registration failed');
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Request failed. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      <Toaster
        position="top-center"
        toastOptions={{
          style: {
            background: 'var(--popover)',
            color: '#fff',
            border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: '10px',
            fontSize: '14px',
            backdropFilter: 'blur(12px)',
          },
          success: { iconTheme: { primary: 'var(--primary)', secondary: 'var(--primary-foreground)' } },
          error: { iconTheme: { primary: 'var(--destructive)', secondary: '#fff' } },
          duration: 4000,
        }}
      />
      <div className={styles.bg}>
        <Image src="/bg.png" alt="" fill priority className={styles.bgImg} />
        <div className={styles.bgOverlay} />
        <div className={styles.blob1} />
        <div className={styles.blob2} />
        <div className={styles.blob3} />
      </div>

      <motion.div
        className={styles.card}
        initial={{ opacity: 0, scale: 0.95, y: 30 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
      >
        <Link to="/" className={styles.logo} aria-label="Go to home">
          <img
            src="/cast-logo-full.png"
            alt="BoldscholarCast"
            className={styles.logoImg}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <div className={styles.logoFallback}></div>
        </Link>

        {pendingActivationEmail ? (
          <>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
              <FiCheckCircle size={40} color="var(--primary)" />
            </div>
            <h1 className={styles.heading}>Check Your Email</h1>
            <p className={styles.sub}>
              We sent an activation link to <strong>{pendingActivationEmail}</strong>. Click it to activate
              your account. Once activated, an admin will still need to review your creator account and
              grant you upload access before you can publish anything — you'll get an email when that happens.
            </p>
            <button
              type="button"
              className={styles.submitBtn}
              disabled={resendLoading}
              onClick={handleResendActivation}
            >
              {resendLoading ? <span className={styles.spinner} /> : 'Resend Activation Email'}
            </button>
            <div className={styles.divider}><span>or</span></div>
            <Link to="/login" className={styles.browseLink}>
              Go to Sign In
            </Link>
          </>
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}>
              <FiFilm size={28} color="var(--primary)" />
            </div>
            <h1 className={styles.heading}>Producers’ Platform</h1>
            <p className={styles.sub}>
              Create an account to upload movies and episodes. New creator accounts are reviewed by
              an admin before upload access is granted.
            </p>

            <form className={styles.form} onSubmit={handleSubmit} noValidate>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="fullName">Full Name</label>
                <div className={styles.inputWrap}>
                  <FiUser className={styles.inputIcon} />
                  <input
                    id="fullName"
                    type="text"
                    className={styles.input}
                    placeholder="Your full name"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    autoComplete="name"
                    required
                  />
                </div>
              </div>

              <div className={styles.field}>
                <label className={styles.label} htmlFor="username">Username</label>
                <div className={styles.inputWrap}>
                  <FiAtSign className={styles.inputIcon} />
                  <input
                    id="username"
                    type="text"
                    className={styles.input}
                    placeholder="e.g. john_doe (3–20 chars)"
                    value={username}
                    onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                    autoComplete="username"
                    maxLength={20}
                    required
                  />
                </div>
                <span className={styles.fieldHint}>Letters, numbers and underscores only</span>
              </div>

              <div className={styles.field}>
                <label className={styles.label} htmlFor="email">Email</label>
                <div className={styles.inputWrap}>
                  <FiMail className={styles.inputIcon} />
                  <input
                    id="email"
                    type="email"
                    className={styles.input}
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    required
                  />
                </div>
              </div>

              <div className={styles.field}>
                <label className={styles.label} htmlFor="password">Password</label>
                <div className={styles.inputWrap}>
                  <FiLock className={styles.inputIcon} />
                  <input
                    id="password"
                    type={showPwd ? 'text' : 'password'}
                    className={styles.input}
                    placeholder="Min. 6 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                    required
                  />
                  <button
                    type="button"
                    className={styles.eyeBtn}
                    onClick={() => setShowPwd((v) => !v)}
                    aria-label={showPwd ? 'Hide password' : 'Show password'}
                  >
                    {showPwd ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </div>

              <motion.button
                type="submit"
                className={styles.submitBtn}
                disabled={loading}
                whileHover={{ scale: loading ? 1 : 1.02 }}
                whileTap={{ scale: loading ? 1 : 0.98 }}
              >
                {loading ? <span className={styles.spinner} /> : 'Create Producer\'s Account'}
              </motion.button>
            </form>

            <div className={styles.divider}><span>or</span></div>

            <Link to="/login" className={styles.browseLink}>
              Already have an account? Sign in
            </Link>
          </>
        )}
      </motion.div>
    </div>
  );
}
